import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, Sparkle, ArrowRight, X, DownloadSimple } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { formatINR } from "../lib/format";

const RANK_BADGES = { 1: "🥇", 2: "🥈", 3: "🥉" };

export default function Leaderboard() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(() => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [drawer, setDrawer] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/performance/leaderboard", { params: { period } }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [period]);

  const recompute = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/performance/compute?period=${period}`);
      const missing = r?.data?.missing || [];
      const count = r?.data?.count || 0;
      if (count > 0) {
        toast.success(`Scores recomputed · ${count} employees ranked`);
      } else if (missing.length) {
        // Show WHY the leaderboard is empty — actionable diagnostic
        toast.warning("Nothing to score", { description: missing.join(" · "), duration: 12000 });
      } else {
        toast.warning("Nothing to score — check that employees and MIS data exist for this month");
      }
      load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Compute failed"); }
    finally { setBusy(false); }
  };

  const openDrawer = async (empId) => {
    try { const { data } = await api.get(`/performance/employee/${empId}/drawer`, { params: { period } }); setDrawer(data); }
    catch (e) { toast.error("Load failed"); }
  };

  const top = data?.employee_of_month;

  return (
    <Layout title="Performance Leaderboard" subtitle="Monthly scoring · MIS · Sales · TAT">
      <div className="flex items-center gap-3 mb-6">
        <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="perf-period"
          className="border border-black/15 rounded-none px-3 py-2 text-sm"/>
        <button onClick={recompute} disabled={busy} data-testid="perf-recompute"
          className="px-3 py-2 border border-black/15 text-xs font-semibold hover:bg-black hover:text-white disabled:opacity-40">
          ↻ {busy ? "Computing…" : "Recompute scores"}
        </button>
      </div>

      {top && (
        <div className="border-4 border-double border-[#D90429] bg-gradient-to-br from-[#FFE5E9] via-white to-[#FFE5E9] p-6 mb-6" data-testid="eom-banner">
          <div className="flex items-start gap-4">
            <div className="text-6xl">🏆</div>
            <div className="flex-1">
              <div className="overline text-[10px] tracking-widest text-[#D90429] font-mono-shelv">Employee of the Month · {period}</div>
              <div className="font-display text-3xl font-black tracking-tighter">{top.employee_name}</div>
              <div className="text-sm text-[#4A4D57] mt-1">{top.department} · {top.role_type === "sales" ? "Sales" : "Non-Sales"}</div>
              <p className="text-sm mt-2 italic text-[#0A0A0A]">{data.praise}</p>
              <div className="mt-3 flex flex-wrap gap-3 text-xs font-mono-shelv items-center">
                <span className="px-2 py-1 bg-[#0D9488] text-white">MIS {top.mis_pct}%</span>
                <span className="px-2 py-1 bg-[#0D9488] text-white">TAT {top.tat_pct}%</span>
                {top.role_type === "sales" && <span className="px-2 py-1 bg-[#D90429] text-white">Sales {top.sales_realization_pct}%</span>}
                <span className="px-2 py-1 bg-black text-white">Total {top.total_score} pts</span>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/performance/pdf/${period}`}
                  download={`EmployeeOfMonth_${period}.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="eom-pdf-btn"
                  className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 bg-[#D90429] text-white text-xs font-semibold hover:bg-[#EF233C]"
                >
                  <DownloadSimple size={12} weight="bold"/> Download One-Pager PDF
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="border border-black/10 bg-white overflow-x-auto" data-testid="leaderboard-table">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-[#FAFBFC] text-left text-[10px] uppercase tracking-widest text-[#717680] font-mono-shelv">
            <tr>
              <th className="px-3 py-2 w-12">Rank</th>
              <th className="px-3 py-2">Employee</th>
              <th className="px-3 py-2">Dept</th>
              <th className="px-3 py-2 text-right">Tata MIS %</th>
              <th className="px-3 py-2 text-right">Sales Realization</th>
              <th className="px-3 py-2 text-right">Task TAT %</th>
              <th className="px-3 py-2 text-right">Total Points</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(!data || data.rows.length === 0) ? (
              <tr><td colSpan={8} className="px-3 py-10 text-center" data-testid="lb-empty-state">
                <div className="text-[#0A0A0A] font-semibold mb-2">No scorecards for {period}</div>
                {data?.diagnostics ? (
                  <div className="text-xs text-[#717680] max-w-md mx-auto space-y-1">
                    <div>
                      <span className={data.diagnostics.employees_active > 0 ? "text-emerald-600" : "text-[#D90429]"}>
                        {data.diagnostics.employees_active > 0 ? "✓" : "✗"} {data.diagnostics.employees_active} employees registered
                      </span>
                    </div>
                    <div>
                      <span className={data.diagnostics.mis_logs_this_period > 0 ? "text-emerald-600" : "text-[#D90429]"}>
                        {data.diagnostics.mis_logs_this_period > 0 ? "✓" : "✗"} {data.diagnostics.mis_logs_this_period} MIS logs this period
                      </span>
                    </div>
                    <div>
                      <span className={data.diagnostics.tasks_this_period > 0 ? "text-emerald-600" : "text-[#D90429]"}>
                        {data.diagnostics.tasks_this_period > 0 ? "✓" : "✗"} {data.diagnostics.tasks_this_period} tasks this period
                      </span>
                    </div>
                    {data.diagnostics.employees_active === 0 && (
                      <div className="mt-3 pt-3 border-t border-black/5">
                        <a href="/hr/performance" data-testid="lb-setup-link" className="inline-block bg-[#D90429] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-black">
                          Start here → Add first employee
                        </a>
                      </div>
                    )}
                    <div className="mt-2 text-[#717680]">Click <b>Recompute scores</b> after data is added.</div>
                  </div>
                ) : (
                  <div className="text-xs text-[#717680]">Click <b>Recompute scores</b> to generate.</div>
                )}
              </td></tr>
            ) : data.rows.map((r) => (
              <tr key={r.employee_id} onClick={() => openDrawer(r.employee_id)} className="border-t border-black/5 hover:bg-[#FAFBFC] cursor-pointer" data-testid={`lb-row-${r.employee_id}`}>
                <td className="px-3 py-2 text-center text-2xl">{RANK_BADGES[r.rank] || <span className="text-sm font-mono-shelv">#{r.rank}</span>}</td>
                <td className="px-3 py-2 font-semibold">{r.employee_name}<div className="text-[10px] text-[#717680]">{r.role_type === "sales" ? "Sales role" : "Non-Sales role"}</div></td>
                <td className="px-3 py-2 text-xs">{r.department}</td>
                <td className="px-3 py-2 text-right font-mono-shelv" style={{ color: r.mis_pct >= 90 ? "#0D9488" : r.mis_pct >= 70 ? "#F4A261" : "#D90429" }}>{r.mis_pct}%</td>
                <td className="px-3 py-2 text-right font-mono-shelv">{r.role_type === "sales" ? `${r.sales_realization_pct}%` : <span className="text-[#717680]">n/a</span>}</td>
                <td className="px-3 py-2 text-right font-mono-shelv" style={{ color: r.tat_pct >= 85 ? "#0D9488" : r.tat_pct >= 70 ? "#F4A261" : "#D90429" }}>{r.tat_pct}%</td>
                <td className="px-3 py-2 text-right font-mono-shelv font-bold text-lg">{r.total_score}</td>
                <td className="px-3 py-2 text-right"><ArrowRight size={12} className="text-[#717680]"/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {drawer && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setDrawer(null)}>
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="perf-drawer">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline text-[10px]">Performance breakdown · {drawer.period}</div>
                <div className="font-display text-2xl font-black">{drawer.scorecard?.employee_name}</div>
              </div>
              <button onClick={() => setDrawer(null)} className="text-[#717680] hover:text-black"><X size={20}/></button>
            </div>

            {drawer.scorecard ? (
              <>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <Stat label="MIS" val={`${drawer.scorecard.mis_score} pts`} sub={`${drawer.scorecard.mis_pct}%`}/>
                  <Stat label="TAT" val={`${drawer.scorecard.tat_score} pts`} sub={`${drawer.scorecard.tat_pct}%`}/>
                  <Stat label={drawer.scorecard.role_type === "sales" ? "Sales" : "Non-Sales"} val={`${drawer.scorecard.sales_score + drawer.scorecard.non_sales_score} pts`} sub={drawer.scorecard.role_type === "sales" ? `${drawer.scorecard.sales_realization_pct}%` : "task volume"}/>
                </div>
                <div className="border-t border-black/10 pt-4">
                  <div className="overline text-[10px] mb-2">MIS Issues ({drawer.mis_issues.length})</div>
                  {drawer.mis_issues.length === 0 ? <div className="text-xs text-[#717680] italic">No missed / late MIS logs</div> : (
                    <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                      {drawer.mis_issues.map((m) => (
                        <div key={m.id} className="flex items-center justify-between border-b border-black/5 py-1">
                          <span>{m.date} · {m.assigned_account || "—"}</span>
                          <span className="uppercase font-mono-shelv text-[10px] text-[#D90429]">{m.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="border-t border-black/10 pt-4 mt-4">
                  <div className="overline text-[10px] mb-2">Tasks ({drawer.tasks.length})</div>
                  {drawer.tasks.length === 0 ? <div className="text-xs text-[#717680] italic">No tasks logged</div> : (
                    <div className="max-h-60 overflow-y-auto text-xs space-y-1">
                      {drawer.tasks.map((t) => (
                        <div key={t.id} className="flex items-center justify-between border-b border-black/5 py-1">
                          <span>{t.date_assigned} · {t.task_category || "task"}</span>
                          <span className="uppercase font-mono-shelv text-[10px]" style={{ color: t.sla_status === "within_sla" ? "#0D9488" : "#D90429" }}>{t.sla_status.replace(/_/g, " ")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : <div className="text-sm text-[#717680]">No scorecard for this employee in {drawer.period}. Recompute scores first.</div>}
          </div>
        </div>
      )}
    </Layout>
  );
}
function Stat({ label, val, sub }) {
  return (
    <div className="border border-black/10 p-2">
      <div className="overline text-[10px] text-[#717680]">{label}</div>
      <div className="font-display font-black text-lg">{val}</div>
      <div className="text-[10px] text-[#717680]">{sub}</div>
    </div>
  );
}
